/**
 * Edit Provider Dialog
 *
 * Form dialog for updating an existing provider profile.
 * Sends data to PATCH /api/providers/<uuid>/update/ via useProviderActions.
 *
 * The backend accepts: name, phone_number, availability_status, max_travel_distance, entity_id.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Save, X, Phone, Mail, User } from 'lucide-react'
import {
  AVAILABILITY_STATUSES,
  type UpdateProviderInput,
  type AvailabilityStatus,
} from '@/types/provider'
import type { OrganizationProviderRow } from '@/hooks/useOrganizationProviders'
import { useLocalization } from '@/contexts/LocalizationContext'
import {
  getProviderTypeDisplay,
  getAvailabilityStatusDisplay,
  getProviderStatusDisplay,
} from '@/utils/providerLocalization'

/** Backend expects lowercase; normalize from API (e.g. "Busy" → "busy"). */
function normalizeAvailabilityStatus(
  value: string | null | undefined
): AvailabilityStatus {
  const lower = (value ?? '').toString().trim().toLowerCase()
  if (AVAILABILITY_STATUSES.includes(lower as AvailabilityStatus)) {
    return lower as AvailabilityStatus
  }
  return 'available'
}

interface EditProviderDialogProps {
  provider: OrganizationProviderRow | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (providerId: string, input: UpdateProviderInput) => Promise<void>
  isLoading?: boolean
}

export function EditProviderDialog({
  provider,
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: EditProviderDialogProps) {
  const { t } = useLocalization()
  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState({
    name: '',
    phone_number: '',
    availability_status: 'available' as AvailabilityStatus,
    max_travel_distance: '',
  })

  // Sync form with provider when dialog opens (normalize status so dropdown matches)
  useEffect(() => {
    if (provider && open) {
      setForm({
        name: provider.fullName || provider.name || '',
        phone_number: provider.phoneNumber || '',
        availability_status: normalizeAvailabilityStatus(
          provider.availabilityStatus ?? undefined
        ),
        max_travel_distance: '',
      })
      setIsEditing(false)
    }
  }, [provider, open])

  const handleChange = (field: keyof typeof form, value: string) => {
    if (field === 'phone_number') {
      const cleaned = value.replace(/[^\d+]/g, '')
      setForm((prev) => ({ ...prev, [field]: cleaned }))
    } else {
      setForm((prev) => ({ ...prev, [field]: value }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!provider?.id) return

    const input: UpdateProviderInput = {}

    // Only send changed fields
    const originalName = provider.fullName || provider.name || ''
    if (form.name.trim() && form.name.trim() !== originalName) {
      input.name = form.name.trim()
    }
    if (
      form.phone_number.trim() &&
      form.phone_number.trim() !== (provider.phoneNumber || '')
    ) {
      input.phone_number = form.phone_number.trim()
    }
    const currentStatus = normalizeAvailabilityStatus(
      provider.availabilityStatus ?? undefined
    )
    const requestedStatus = normalizeAvailabilityStatus(form.availability_status)
    if (requestedStatus !== currentStatus) {
      input.availability_status = requestedStatus
    }
    if (form.max_travel_distance) {
      input.max_travel_distance = parseInt(form.max_travel_distance, 10)
    }

    // Only submit if there are changes
    if (Object.keys(input).length === 0) {
      setIsEditing(false)
      return
    }

    await onSubmit(provider.id, input)
    setIsEditing(false)
  }

  if (!provider) return null

  const statusText = getProviderStatusDisplay(provider.status, provider.statusDisplay, t)
  const typeDisplay = getProviderTypeDisplay(provider.providerType, provider.providerTypeDisplay, t)
  const availabilityDisplay = getAvailabilityStatusDisplay(
    provider.availabilityStatus,
    provider.availabilityStatusDisplay,
    t
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('providers.providerDetails')}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('providers.editDescription')
              : t('providers.viewDescription')}
          </DialogDescription>
        </DialogHeader>

        {isEditing ? (
          /* ── Edit Mode ──────────────────────────────────────────── */
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="edit-name">{t('providers.fullName')}</Label>
                <Input
                  id="edit-name"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-phone">{t('providers.phoneNumber')}</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={form.phone_number}
                  onChange={(e) =>
                    handleChange('phone_number', e.target.value)
                  }
                  placeholder="+966501234567"
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  {t('providers.phoneFormat')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-availability">{t('providers.availabilityStatus')}</Label>
                <select
                  id="edit-availability"
                  value={form.availability_status}
                  onChange={(e) =>
                    handleChange('availability_status', e.target.value)
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isLoading}
                >
                  {AVAILABILITY_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`providers.availabilityStatuses.${s}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="edit-travel">{t('providers.maxTravelDistance')}</Label>
                <Input
                  id="edit-travel"
                  type="number"
                  min={0}
                  value={form.max_travel_distance}
                  onChange={(e) =>
                    handleChange('max_travel_distance', e.target.value)
                  }
                  placeholder={t('providers.leaveEmptyToKeepUnchanged')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
                disabled={isLoading}
              >
                <X className="h-4 w-4 me-2" />
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isLoading}>
                <Save className="h-4 w-4 me-2" />
                {isLoading ? t('common.saving') : t('providers.saveChanges')}
              </Button>
            </div>
          </form>
        ) : (
          /* ── View Mode ──────────────────────────────────────────── */
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary text-white font-semibold text-lg">
                    {(provider.fullName || provider.name || 'P')
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">
                      {provider.fullName || provider.name || '—'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {typeDisplay}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {provider.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span>{provider.email}</span>
                    </div>
                  )}
                  {provider.phoneNumber && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="font-mono">{provider.phoneNumber}</span>
                    </div>
                  )}
                  {provider.organizationName && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span>{provider.organizationName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">
                    {t('common.status')}
                  </h4>
                  <Badge
                    variant={
                      (provider.status || provider.statusDisplay || '').toLowerCase().includes('active') &&
                      !(provider.status || provider.statusDisplay || '').toLowerCase().includes('inactive')
                        ? 'default'
                        : 'secondary'
                    }
                  >
                    {statusText}
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">
                    {t('providers.availability')}
                  </h4>
                  <Badge
                    variant={provider.isAvailable ? 'default' : 'secondary'}
                  >
                    {availabilityDisplay !== '—' ? availabilityDisplay : (provider.isAvailable ? t('providers.available') : t('providers.unavailable'))}
                  </Badge>
                </div>
                {provider.rating != null && provider.rating > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">
                      {t('providers.rating')}
                    </h4>
                    <span className="font-medium">
                      {provider.rating.toFixed(1)}
                    </span>
                    {provider.totalReviews != null && (
                      <span className="text-xs text-muted-foreground ms-1">
                        ({provider.totalReviews} {t('providers.reviews')})
                      </span>
                    )}
                  </div>
                )}
                {provider.backgroundCheckStatusDisplay && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">
                      {t('providers.backgroundCheck')}
                    </h4>
                    <span className="text-sm">
                      {provider.backgroundCheckStatusDisplay}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t('common.close')}
              </Button>
              <Button onClick={() => setIsEditing(true)}>{t('providers.editProvider')}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
